import { type Event } from '@botpress/client'
import {
  Button,
  Card,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { useQuery, useSuspenseInfiniteQuery, useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  BoxIcon,
  DatabaseIcon,
  MessageSquareMoreIcon,
  RadioIcon,
  RefreshCwIcon,
  UploadIcon,
  ZapIcon,
} from 'lucide-react'
import { DateTime } from 'luxon'
import {
  ComponentProps,
  FC,
  Fragment,
  memo,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { Boundary, type FilterMenuInput, Page, QueryParamBuilder } from '~/components'
import { Icon, Spinner } from '~/elements'
import { Badge } from '~/elements/Badge'
import { JSONTree } from '~/elements/JSONTree'
import { statusDetails, statuses } from '~/features/events/constants'
import { useInfiniteScroll, useQueryKeys } from '~/hooks'
import { useIntegrations } from '~/hooks/integrations/useIntegrations'
import { botQueryOptions, listWorkspaceIntegrationsQueryOptions } from '~/queries'
import { getEventQueryOptions, listEventsInfiniteQueryOptions } from '~/queries/events'
import { useSuspenseQuery } from '~/services'
import { Color } from '~/types'
import { cn } from '~/utils'

type EventListHandle = {
  refetch: () => void
}

const baseEventTypes = ['state_expired', 'message_created', 'task_update'] as const

const eventsSearchSchema = z.object({
  conversationId: z.string().optional(),
  messageId: z.string().optional(),
  type: z.string().optional(),
  userId: z.string().optional(),
  eventId: z.string().optional(),
  statuses: z
    .enum(statuses)
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/events')({
  validateSearch: eventsSearchSchema,
  component: Component,
})

function Component() {
  return (
    <Boundary height={'200px'} loaderSize="6">
      <EventsPage />
    </Boundary>
  )
}

const EventsPage = () => {
  const { workspaceId, botId } = Route.useParams()
  const { queryClient } = Route.useRouteContext()

  const [lastFetch, setLastFetch] = useState(Date.now())
  const [isFetching, setIsFetching] = useState(false)
  const eventsListRef = useRef<EventListHandle>(null)

  const bot = useTanstackSuspenseQuery(botQueryOptions({ workspaceId, botId })).data
  const botIntegrations = useIntegrations({
    workspaceId,
    integrationIds: Object.values(bot.integrations).map((i) => i.id),
  })

  const botEvents = Object.keys(bot.events)
  const botIntegrationEvents = botIntegrations.flatMap((integration) =>
    Object.keys(integration.events).map((e) => `${integration.name}:${e}`)
  )

  const filters: FilterMenuInput[] = [
    { name: 'Event', paramName: 'eventId' },
    { name: 'Conversation', paramName: 'conversationId' },
    { name: 'User', paramName: 'userId' },
    { name: 'Message', paramName: 'messageId' },
    {
      name: 'Event Type',
      value: [...baseEventTypes, ...botEvents, ...botIntegrationEvents].map((type) => ({
        name: type,
        paramName: 'type',
        value: type,
        icon: <EventIcon type={type} variant="soft" size="2" />,
      })),
    },
    {
      name: 'Status',
      value: statuses.map((status) => ({ name: statusDetails[status].label, value: status, paramName: 'status' })),
    },
  ]

  return (
    <Page
      title="Events"
      justify="start"
      actions={
        <Button
          variant="ghost"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: useQueryKeys().listEvents({ workspaceId, botId }) })
            eventsListRef.current?.refetch()
          }}
          className="h-fit px-2 py-1"
        >
          <Icon className={cn({ 'animate-spin': isFetching })} icon={RefreshCwIcon} color="gray" />
          <p className="text-xs text-muted-foreground">
            {match({ isFetching, lastFetch })
              .with({ isFetching: true }, () => 'Fetching events...')
              .when(
                ({ lastFetch }) => DateTime.fromMillis(lastFetch).diffNow().milliseconds < -60000,
                () => DateTime.fromMillis(lastFetch).toRelative()
              )
              .otherwise(() => 'A few moments ago')}
          </p>
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <QueryParamBuilder filters={filters} />
        <Boundary loaderSize={'6'}>
          <EventsList ref={eventsListRef} setLastFetch={setLastFetch} setIsFetching={setIsFetching} />
        </Boundary>
      </div>
    </Page>
  )
}

const EventsList = memo(
  ({
    ref,
    setLastFetch,
    setIsFetching,
  }: { setLastFetch: (time: number) => void; setIsFetching: (isFetching: boolean) => void } & {
    ref: Ref<EventListHandle>
  }) => {
    const { workspaceId, botId } = Route.useParams()
    const { eventId, ...queryParams } = Route.useSearch()
    const {
      data: fetchedEvents,
      fetchNextPage,
      refetch,
      hasNextPage,
      dataUpdatedAt,
      isFetching,
    } = useSuspenseInfiniteQuery(
      listEventsInfiniteQueryOptions({
        workspaceId,
        botId,
        ...queryParams,
      })
    )

    const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

    const { data: fetchedEvent } = useQuery({
      ...getEventQueryOptions({ workspaceId, botId, eventId: eventId ?? '' }),
      enabled: !!eventId,
    })
    const events = useMemo(
      () => (fetchedEvent?.event ? [fetchedEvent.event] : fetchedEvents.pages.flatMap((p) => p.events)),
      [fetchedEvents.pages, fetchedEvent]
    )

    useImperativeHandle(ref, () => ({
      refetch,
    }))

    useEffect(() => {
      if (dataUpdatedAt) {
        setLastFetch(dataUpdatedAt)
      }
    }, [dataUpdatedAt])

    useEffect(() => {
      setIsFetching(isFetching)
    }, [isFetching])

    return (
      <>
        <Card className="overflow-hidden rounded-md p-0">
          <div className="relative grid grid-cols-[repeat(3,auto)_1fr_auto_1fr] gap-x-10 text-sm">
            <div className="col-span-full grid grid-cols-subgrid bg-neutral-50 p-2 font-medium">
              <div />
              <p>Event</p>
              <p>Time</p>
              <p>Event Identifier</p>
              <p className="text-center">Status</p>
              <p>Failure Reason</p>
            </div>
            {events.map((event) => (
              <Fragment key={event.id}>
                <Separator className="col-span-full" />
                <EventRow event={event} />
              </Fragment>
            ))}
          </div>
        </Card>
        <div ref={bottomRef} />
        {isFetching && (
          <div className="flex w-full items-center justify-center">
            <Spinner size="6" />
          </div>
        )}
      </>
    )
  }
)
EventsList.displayName = 'EventsList'

const EventRow = ({ event }: { event: Event }) => {
  const { createdAt, failureReason, id, status, type } = event
  const [showJSON, setShowJSON] = useState(false)

  return (
    <>
      <div
        className="col-span-full grid grid-cols-subgrid items-center p-2 text-muted-foreground hover:cursor-pointer hover:bg-neutral-50"
        onClick={() => {
          setShowJSON((prev) => !prev)
        }}
      >
        <EventIcon type={type} variant="soft" size="3" className="-mr-8" />
        <p className="text-foreground">{type}</p>
        <p>{DateTime.fromISO(createdAt).toRelative()}</p>
        <code className="truncate">{id}</code>
        <Tooltip>
          <TooltipTrigger>
            <Badge color={statusDetails[status].color} variant="secondary" className="w-fit place-self-center">
              {statusDetails[status].label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">{statusDetails[status].description}</TooltipContent>
        </Tooltip>
        <HoverCard openDelay={400} closeDelay={200}>
          <HoverCardTrigger>
            <code className="truncate text-xs">{failureReason}</code>
          </HoverCardTrigger>
          <HoverCardContent
            onClick={(e) => {
              e.stopPropagation()
            }}
            side="top"
            align="start"
            className="w-fit max-w-[400px]"
          >
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <p className="text-sm">{type}</p>
                <Badge color={statusDetails[status].color} variant="secondary" className="w-fit">
                  {statusDetails[status].label}
                </Badge>
              </div>
              <code className="truncate text-xs">{failureReason}</code>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
      {showJSON ? (
        <>
          <Separator className="col-span-full" />
          <div className="col-span-full p-4">
            <JSONTree
              hideRoot
              data={
                event.type === 'state_expired'
                  ? {
                      ...event,
                      payload: {
                        ...event.payload,
                        state: { ...event.payload.state, payload: JSON.parse(event.payload?.state?.payload) },
                      },
                    }
                  : event
              }
            />
          </div>
        </>
      ) : null}
    </>
  )
}

const EventIcon = ({ type, ...iconProps }: { type: string } & Omit<ComponentProps<typeof Icon>, 'icon'>) => {
  const { workspaceId } = Route.useParams()
  const integrations = useSuspenseQuery('integrations_').data
  const workspaceIntegrations = useTanstackSuspenseQuery(listWorkspaceIntegrationsQueryOptions({ workspaceId })).data
  const integrationIcons = [...integrations, ...workspaceIntegrations].reduce(
    (acc, integration) => {
      acc[integration.name] = integration.iconUrl
      return acc
    },
    {} as Record<string, string>
  )

  const [EventIcon, color]: [FC<{ className?: string }>, Color] = match(type)
    .with('state_expired', () => [DatabaseIcon, 'red'] as [FC<{ className?: string }>, Color])
    .with('message_created', () => [MessageSquareMoreIcon, 'blue'] as [FC<{ className?: string }>, Color])
    .with('task_update', () => [RadioIcon, 'blue'] as [FC<{ className?: string }>, Color])
    .with('botready', () => [ZapIcon, 'green'] as [FC<{ className?: string }>, Color])
    .with('botpublished', () => [UploadIcon, 'green'] as [FC<{ className?: string }>, Color])
    .when(
      (type) => type.split(':').length === 2,
      () => {
        const integrationIconLink = integrationIcons[type.split(':')[0] ?? '']
        const Icon = integrationIconLink
          ? ({ className, ...props }: { className: string }) => (
              <img src={integrationIconLink} {...props} className={cn('p-0.5', className)} aria-label="Event Icon" />
            )
          : BoxIcon
        return [Icon, 'gray'] as [FC<{ className?: string }>, Color]
      }
    )
    .otherwise(() => [RadioIcon, 'green'] as [FC<{ className?: string }>, Color])

  return <Icon icon={EventIcon} color={color} {...iconProps} />
}
